import React, { useEffect, useMemo, useRef, useState } from "react";
import Head from "next/head";
import Link from "next/link";
import Pagination from "./pagination";
import { VIDEO_BLOG_INDEX } from "./video-blog-index-data";
import VideoBlogIndexThumb, {
	getVideoBlogIndexThumbPreload,
} from "./video-blog-index-thumb";
import SeoJsonLd from "./jsonld/seo-jsonld";

const PAGE_SIZE = 6;

function normalizeForSearch(text) {
	return text
		.toLowerCase()
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "");
}

function matchesSearch(title, term) {
	if (!term) return true;
	const raw = term.toLowerCase();
	const normalized = normalizeForSearch(term);
	const titleLower = title.toLowerCase();
	const titleNorm = normalizeForSearch(title);
	return (
		titleLower.includes(raw) ||
		titleNorm.includes(normalized) ||
		title.normalize("NFKD").toLowerCase().includes(raw) ||
		title.normalize("NFKC").toLowerCase().includes(raw)
	);
}

export default function VideoBlogIndex({
	locale = "en",
	db = [],
	lcpThumbPreload: staticLcpThumbPreload = null,
}) {
	const copy = VIDEO_BLOG_INDEX[locale] || VIDEO_BLOG_INDEX.en;
	const ogLocale = locale === "es" ? "es_ES" : "en";

	const [currentPage, setCurrentPage] = useState(1);
	const [searchTerm, setSearchTerm] = useState("");
	const prevPageRef = useRef(currentPage);

	const filteredDb = useMemo(
		() => db.filter((item) => matchesSearch(item.title, searchTerm.trim())),
		[db, searchTerm],
	);

	const totalPages = Math.max(1, Math.ceil(filteredDb.length / PAGE_SIZE));

	const currentPosts = useMemo(() => {
		const start = (currentPage - 1) * PAGE_SIZE;
		return filteredDb.slice(start, start + PAGE_SIZE);
	}, [currentPage, filteredDb]);

	const lcpThumbPreload = useMemo(() => {
		if (currentPage !== 1 || searchTerm.trim()) return null;
		return (
			staticLcpThumbPreload ??
			getVideoBlogIndexThumbPreload(currentPosts[0]?.picture)
		);
	}, [currentPage, searchTerm, staticLcpThumbPreload, currentPosts]);

	useEffect(() => {
		setCurrentPage((prev) => Math.min(Math.max(1, prev), totalPages));
	}, [totalPages]);

	useEffect(() => {
		if (prevPageRef.current === currentPage) return;
		prevPageRef.current = currentPage;
		window.scrollTo(0, 0);
	}, [currentPage]);

	return (
		<section className="tu-blog-index" lang={locale}>
			<Head>
				<meta charSet="UTF-8" />
				<meta name="viewport" content="width=device-width, initial-scale=1" />
				<link rel="shortcut icon" type="image/png" href="/images/tu-favicon.png" />
				<meta
					name="facebook-domain-verification"
					content="fzctnjbrtlybvytmamk8glkng9af7p"
				/>
				<title>{copy.pageTitle}</title>
				<meta name="description" content={copy.metaDescription} />
				<meta name="keywords" content={copy.metaKeywords} />
				<meta name="author" content="Edgar Lindo" />
				<meta property="og:title" content={copy.ogTitle} />
				<meta property="og:description" content={copy.ogDescription} />
				<meta property="og:url" content={copy.ogUrl} />
				<meta property="og:locale" content={ogLocale} />
				<meta property="site_name" content="TU" />
				<meta property="og:image" content={copy.ogImage} />
				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:creator" content="@MrEdgarLindo" />
				<meta name="twitter:title" content={copy.twitterTitle} />
				<meta name="twitter:description" content={copy.twitterDescription} />
				<meta name="twitter:image" content={copy.ogImage} />
				{lcpThumbPreload ? (
					<>
						<link
							rel="preload"
							as="image"
							href={lcpThumbPreload.href}
							imageSrcSet={lcpThumbPreload.imageSrcSet}
							imageSizes={lcpThumbPreload.imageSizes}
							media="(max-width: 991px)"
							fetchPriority="high"
						/>
						{lcpThumbPreload.desktopHref ? (
							<link
								rel="preload"
								as="image"
								href={lcpThumbPreload.desktopHref}
								imageSrcSet={lcpThumbPreload.desktopSrcSet}
								imageSizes={lcpThumbPreload.desktopSizes}
								media="(min-width: 992px)"
								fetchPriority="high"
							/>
						) : null}
					</>
				) : null}
			</Head>

			<SeoJsonLd locale={locale} pageId={locale === "es" ? "video-blog" : "video-blogs"} />

			<div className="tu-container">
				<div className="tu-blog-index__layout">
					<div className="tu-blog-index__main">
						<header className="tu-blog-index__header">
							<h1 className="tu-blog-index__title">{copy.title}</h1>
							<p className="tu-blog-index__intro">{copy.intro}</p>
						</header>

						<div className="tu-blog-index__toolbar">
							<label className="tu-blog-index__search-wrap">
								<span className="tu-blog-index__search-label">
									{copy.searchTitle}
								</span>
								<input
									type="search"
									className="tu-blog-index__search form-control"
									value={searchTerm}
									onChange={(e) => {
										setSearchTerm(e.target.value);
										setCurrentPage(1);
									}}
									placeholder={copy.searchPlaceholder}
									aria-label={copy.searchTitle}
								/>
							</label>
							<p className="tu-blog-index__count" aria-live="polite">
								{copy.resultsLabel(filteredDb.length)}
							</p>
						</div>

						{currentPosts.length === 0 ? (
							<p className="tu-blog-index__empty">{copy.emptyResults}</p>
						) : (
							<ul className="tu-blog-index__grid">
								{currentPosts.map((item, index) => (
									<li key={item.id || item.link}>
										<article className="tu-blog-index__card">
											<Link
												href={item.link}
												className="tu-blog-index__thumb"
												prefetch={false}
											>
												<VideoBlogIndexThumb
													src={item.picture}
													alt={item.alt || item.title}
													priority={index === 0 && currentPage === 1}
												/>
											</Link>
											<div className="tu-blog-index__card-body">
												<h2 className="tu-blog-index__card-title">
													<Link href={item.link} prefetch={false}>
														{item.title}
													</Link>
												</h2>
												<Link
													href={item.link}
													className="tu-btn tu-btn-primary tu-blog-index__cta"
													prefetch={false}
												>
													{item.more || item.verMas || copy.readMore}
												</Link>
											</div>
										</article>
									</li>
								))}
							</ul>
						)}

						{filteredDb.length > PAGE_SIZE ? (
							<div className="tu-blog-index__pagination">
								<Pagination
									className="pagination-bar"
									currentPage={currentPage}
									totalCount={filteredDb.length}
									pageSize={PAGE_SIZE}
									onPageChange={setCurrentPage}
								/>
							</div>
						) : null}
					</div>
				</div>
			</div>
		</section>
	);
}
